import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, CircleX, Clock, Loader2, MinusCircle } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import type { FlowRunNodeStatus, FlowRunStatus } from '@reflex/flows';

// Exhaustively keyed over run AND run-node statuses (the badge renders
// both), so adding a status fails compilation here instead of falling
// through to the raw-text default at runtime.
const statusConfig: Record<
  FlowRunStatus | FlowRunNodeStatus,
  { label: string; className: string; textClassName: string; icon: ReactNode }
> = {
  running: {
    label: 'Running',
    className: 'bg-status-info/10 text-status-info',
    textClassName: 'text-status-info',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  waiting_approval: {
    label: 'Awaiting Approval',
    className: 'bg-status-warning/10 text-status-warning',
    textClassName: 'text-status-warning',
    icon: <Clock className="h-3 w-3" />,
  },
  pending_consent: {
    label: 'Awaiting Consent',
    className: 'bg-status-warning/10 text-status-warning',
    textClassName: 'text-status-warning',
    icon: <Clock className="h-3 w-3" />,
  },
  completed: {
    label: 'Completed',
    className: 'bg-status-success/15 text-status-success',
    textClassName: 'text-status-success',
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  error: {
    label: 'Error',
    className: 'bg-[#fdefec] text-[#B53F35] dark:bg-[#B53F35]/15 dark:text-[#e07a74]',
    textClassName: 'text-[#B53F35] dark:text-[#e07a74]',
    icon: <AlertCircle className="h-3 w-3" />,
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-status-neutral/15 text-status-neutral',
    textClassName: 'text-muted-foreground',
    icon: <MinusCircle className="h-3 w-3" />,
  },
  denied: {
    label: 'Denied',
    className: 'bg-[#fdefec] text-[#B53F35] dark:bg-[#B53F35]/15 dark:text-[#e07a74]',
    textClassName: 'text-[#B53F35] dark:text-[#e07a74]',
    icon: <CircleX className="h-3 w-3" />,
  },
  pending: {
    label: 'Pending',
    className: 'bg-status-neutral/15 text-status-neutral',
    textClassName: 'text-muted-foreground',
    icon: <Clock className="h-3 w-3" />,
  },
  skipped: {
    label: 'Skipped',
    className: 'bg-status-neutral/15 text-status-neutral',
    textClassName: 'text-muted-foreground',
    icon: <MinusCircle className="h-3 w-3" />,
  },
};

interface FlowStatusBadgeProps {
  status: string;
  className?: string;
  /**
   * `badge` is the pill used on run detail and cards. `text` is the
   * automations-list treatment: colored label, no chrome.
   */
  variant?: 'badge' | 'text';
}

export function FlowStatusBadge({ status, className, variant = 'badge' }: FlowStatusBadgeProps) {
  const config = statusConfig[status as FlowRunStatus | FlowRunNodeStatus] ?? {
    label: status,
    className: 'bg-status-neutral/15 text-status-neutral',
    textClassName: 'text-muted-foreground',
    icon: <MinusCircle className="h-3 w-3" />,
  };
  if (variant === 'text') {
    return (
      <span className={cn('text-xs font-medium', config.textClassName, className)}>
        {config.label}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[4px] overflow-hidden rounded-[8px] px-[8px] py-[2px]',
        config.className,
        className,
      )}
    >
      <span
        className="flex h-3 w-3 shrink-0 items-center justify-center [&>svg]:h-3 [&>svg]:w-3"
        aria-hidden="true"
        data-status-icon={status}
      >
        {config.icon}
      </span>
      <span className="text-[10px] font-semibold leading-[17px] whitespace-nowrap">
        {config.label}
      </span>
    </span>
  );
}
